import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@heroui/react';
import { apiFetch } from '@/lib/api';
import type { Client, TaskType, TemplateStatus, VisitTemplateDetail } from '@/lib/types';
import { PROPOSED_TASKS } from '@/lib/taskCatalog';
import { useUserStore } from '@/store/userSlice';
import { useTemplatesStore, selectTemplates } from '@/store/templatesSlice';
import type { TaskPatch } from './components/TemplateTasks';

export interface TemplateFormValues {
  title: string;
  description: string;
  instruction: string;
  followUp: string;
  cost: string;
  aiContext: string;
  clientId: string;
  nextVisitTemplateId: string;
  color: string;
}

const EMPTY_FORM: TemplateFormValues = {
  title: '',
  description: '',
  instruction: '',
  followUp: '',
  cost: '',
  aiContext: '',
  clientId: '',
  nextVisitTemplateId: '',
  color: '',
};

async function readError(res: Response): Promise<{ status: number; message: string }> {
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return { status: res.status, message: data?.error ?? `Ошибка запроса (${res.status})` };
}

function formFromTemplate(data: VisitTemplateDetail): TemplateFormValues {
  return {
    title: data.title,
    description: data.description ?? '',
    instruction: data.instruction ?? '',
    followUp: data.followUp ?? '',
    cost: data.cost ? String(Number(data.cost)) : '',
    aiContext: data.aiContext ?? '',
    clientId: data.clientId ?? '',
    nextVisitTemplateId: data.nextVisitTemplateId ?? '',
    color: data.color ?? '',
  };
}

export function useTemplateEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isStaff = useUserStore((s) => s.isStaff);
  // Шаблон создаётся кнопкой «Создать шаблон» на списке (сразу, в статусе
  // new) — эта страница всегда редактирует существующий шаблон
  const templateId = id ?? null;

  const [template, setTemplate] = useState<VisitTemplateDetail | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [form, setForm] = useState<TemplateFormValues>(EMPTY_FORM);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<VisitTemplateDetail['photos']>([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(templateId));
  const [notFound, setNotFound] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isTaskMutating, setIsTaskMutating] = useState(false);

  const fetchTemplates = useTemplatesStore((s) => s.fetchTemplates);
  const allTemplates = useTemplatesStore(selectTemplates);

  // Селект цепочки наполняется только запущенными шаблонами (кроме себя)
  const chainTemplates = useMemo(
    () => allTemplates.filter((t) => t.status === 'active' && t.id !== templateId),
    [allTemplates, templateId],
  );

  useEffect(() => {
    if (!isStaff) return;
    void apiFetch('/api/v1/admin/clients')
      .then((res) => (res.ok ? res.json() : []))
      .then((list: Client[]) => setClients(list))
      .catch(() => setClients([]));
    void fetchTemplates();
  }, [isStaff, fetchTemplates]);

  useEffect(() => {
    // смена маршрута /templates/:id — сброс режима (new вернёт в edit после загрузки)
    setMode('view');
    setTitleError(null);
  }, [templateId]);

  const fetchTemplate = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!templateId) return;
      // silent — обновить template/photos без спиннера и сброса формы: так
      // операции с задачами (и ошибки правок) не затирают несохранённые
      // поля шаблона
      const silent = opts?.silent === true;
      if (!silent) setIsLoading(true);
      setNotFound(false);
      try {
        const res = await apiFetch(`/api/v1/admin/templates/${templateId}`);
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) throw new Error((await readError(res)).message);
        const data = (await res.json()) as VisitTemplateDetail;
        setTemplate(data);
        setPhotos(data.photos ?? []);
        if (!silent) {
          setForm(formFromTemplate(data));
          // Несохранённый (new) всегда в режиме редактирования
          if (data.status === 'new') setMode('edit');
        }
      } catch (e) {
        toast.danger(e instanceof Error ? e.message : 'Не удалось загрузить шаблон');
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [templateId],
  );

  useEffect(() => {
    void fetchTemplate();
  }, [fetchTemplate]);

  const setField = <K extends keyof TemplateFormValues>(key: K, value: TemplateFormValues[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const startEdit = () => {
    if (template) setForm(formFromTemplate(template));
    setTitleError(null);
    setMode('edit');
  };

  // Отмена правок: поля формы возвращаются к сохранённым данным. Задачи и
  // фото сохраняются мгновенно и при отмене не откатываются — список в
  // просмотре обновляется свежим fetch. Несохранённый (new) сбрасывается
  // целиком — архивируется, назад к списку
  const cancelEdit = async () => {
    if (!template) return;
    if (template.status === 'new') {
      try {
        const res = await apiFetch(`/api/v1/admin/templates/${template.id}`, {
          method: 'DELETE',
        });
        if (!res.ok) throw new Error((await readError(res)).message);
      } catch (e) {
        toast.danger(e instanceof Error ? e.message : 'Не удалось удалить шаблон');
        return;
      }
      navigate('/templates', { replace: true });
      return;
    }
    await fetchTemplate();
    setTitleError(null);
    setMode('view');
  };

  // ── Сохранение полей (PATCH; для new — ещё и переход в draft) ──

  const handleSave = async () => {
    if (!template) return;
    if (!form.title.trim()) {
      setTitleError('Укажите название шаблона');
      return;
    }
    const wasNew = template.status === 'new';
    setIsSaving(true);
    setTitleError(null);
    try {
      const payload: Record<string, unknown> = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        instruction: form.instruction.trim() || null,
        follow_up: form.followUp.trim() || null,
        cost: form.cost === '' ? 0 : Number(form.cost),
        ai_context: form.aiContext.trim() || null,
        color: form.color.trim() || null,
      };
      if (isStaff) {
        payload.client_id = form.clientId || null;
        payload.next_visit_template_id = form.nextVisitTemplateId || null;
      }

      const res = await apiFetch(`/api/v1/admin/templates/${template.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const { status, message } = await readError(res);
        // дубль названия — ошибка у поля, остальное — тостом
        if (status === 409 && message.includes('назван')) setTitleError(message);
        else toast.danger(message);
        return;
      }

      if (wasNew) {
        // Первое «Сохранить» шаблона: new → draft (появляется в списке)
        const statusRes = await apiFetch(`/api/v1/admin/templates/${template.id}/status`, {
          method: 'POST',
          body: JSON.stringify({ status: 'draft' }),
        });
        if (!statusRes.ok) {
          // поля сохранены, но шаблон остался new — правки не теряются
          toast.danger((await readError(statusRes)).message);
          return;
        }
        toast.success('Шаблон создан');
        void fetchTemplates();
      } else {
        toast.success('Шаблон сохранён');
      }
      await fetchTemplate();
      setMode('view');
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось сохранить шаблон');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Статусная модель ──────────────────────────────────────────

  const STATUS_TOASTS: Record<TemplateStatus, string> = {
    new: 'Шаблон создан',
    draft: 'Шаблон возвращён в черновик',
    active: 'Шаблон запущен',
    paused: 'Шаблон приостановлен',
  };

  const handleStatusChange = async (status: TemplateStatus) => {
    if (!template) return;
    setIsStatusUpdating(true);
    try {
      const res = await apiFetch(`/api/v1/admin/templates/${template.id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error((await readError(res)).message);
      toast.success(STATUS_TOASTS[status]);
      await fetchTemplate();
      void fetchTemplates();
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось изменить статус');
    } finally {
      setIsStatusUpdating(false);
    }
  };

  // ── Фото (правки — new|draft; сохранение мгновенное) ──────────

  // Загрузка файла: сначала файл в /uploads, потом photo у шаблона
  const handleUploadPhoto = async (file: File) => {
    if (!template) return;
    setIsUploadingPhoto(true);
    try {
      const upload = new FormData();
      upload.append('file', file);
      const uploadRes = await apiFetch('/api/v1/admin/uploads', {
        method: 'POST',
        body: upload,
      });
      if (!uploadRes.ok) throw new Error((await readError(uploadRes)).message);
      const { url } = (await uploadRes.json()) as { url: string };

      const res = await apiFetch(`/api/v1/admin/templates/${template.id}/photos`, {
        method: 'POST',
        body: JSON.stringify({ url, position: photos?.length ?? 0 }),
      });
      if (!res.ok) throw new Error((await readError(res)).message);
      toast.success('Фото загружено');
      // silent: не сбрасывать несохранённые поля формы
      await fetchTemplate({ silent: true });
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось загрузить фото');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!template) return;
    try {
      const res = await apiFetch(`/api/v1/admin/templates/${template.id}/photos/${photoId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error((await readError(res)).message);
      toast.success('Фото удалено');
      await fetchTemplate({ silent: true });
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось удалить фото');
    }
  };

  // ── Задачи (мгновенное сохранение в БД; правки — new|draft) ────

  // Добавление с тулбара: заготовка из каталога, slug генерирует сервер
  const handleAddTask = async (type: TaskType) => {
    if (!template || isTaskMutating) return;
    const proposed = PROPOSED_TASKS.find((p) => p.type === type);
    if (!proposed) return;
    setIsTaskMutating(true);
    try {
      const res = await apiFetch(`/api/v1/admin/templates/${template.id}/task-templates`, {
        method: 'POST',
        body: JSON.stringify({
          type: proposed.type,
          title: proposed.title,
          position: template.task_templates?.length ?? 0,
          is_required: true,
        }),
      });
      if (!res.ok) throw new Error((await readError(res)).message);
      // полный ряд задачи (slug и т.д.) приходит только из GET — тихо
      // обновляем без сброса формы
      await fetchTemplate({ silent: true });
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось добавить задачу');
    } finally {
      setIsTaskMutating(false);
    }
  };

  // Правка полей: оптимистичный патч локального template (чекбокс и селект
  // типа контролируются пропсами задачи) + PATCH без refetch — draft в
  // карточке и есть источник правды; ошибка → тост и синхронизация с сервером
  const handleUpdateTask = async (taskId: string, patch: TaskPatch) => {
    if (!template) return;
    const payload: Record<string, unknown> = {};
    if (patch.title !== undefined) payload.title = patch.title;
    if (patch.type !== undefined) payload.type = patch.type;
    if (patch.instruction !== undefined) payload.instruction = patch.instruction;
    if (patch.aiPrompts !== undefined) payload.ai_prompts = patch.aiPrompts;
    if (patch.isRequired !== undefined) payload.is_required = patch.isRequired;
    setTemplate((prev) =>
      prev
        ? {
            ...prev,
            task_templates: (prev.task_templates ?? []).map((t) =>
              t.id === taskId ? { ...t, ...patch } : t,
            ),
          }
        : prev,
    );
    try {
      const res = await apiFetch(
        `/api/v1/admin/templates/${template.id}/task-templates/${taskId}`,
        { method: 'PATCH', body: JSON.stringify(payload) },
      );
      if (!res.ok) throw new Error((await readError(res)).message);
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось сохранить задачу');
      await fetchTemplate({ silent: true });
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!template || isTaskMutating) return;
    // оптимистично: карточка исчезает сразу
    setTemplate((prev) =>
      prev
        ? { ...prev, task_templates: (prev.task_templates ?? []).filter((t) => t.id !== taskId) }
        : prev,
    );
    setIsTaskMutating(true);
    try {
      const res = await apiFetch(
        `/api/v1/admin/templates/${template.id}/task-templates/${taskId}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error((await readError(res)).message);
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось удалить задачу');
      await fetchTemplate({ silent: true });
    } finally {
      setIsTaskMutating(false);
    }
  };

  // Перестановка ▲/▼: оптимистичный swap + нормализованные позиции 0..n-1
  const handleMoveTask = async (taskId: string, dir: -1 | 1) => {
    if (!template || isTaskMutating) return;
    const tasks = [...(template.task_templates ?? [])];
    const index = tasks.findIndex((t) => t.id === taskId);
    const target = index + dir;
    if (index < 0 || target < 0 || target >= tasks.length) return;
    [tasks[index], tasks[target]] = [tasks[target], tasks[index]];
    setTemplate({ ...template, task_templates: tasks });
    setIsTaskMutating(true);
    try {
      const res = await apiFetch(`/api/v1/admin/templates/${template.id}/task-templates/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ order: tasks.map((t, i) => ({ id: t.id, position: i })) }),
      });
      if (!res.ok) throw new Error((await readError(res)).message);
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось изменить порядок задач');
      await fetchTemplate({ silent: true });
    } finally {
      setIsTaskMutating(false);
    }
  };

  const handleArchive = async () => {
    if (!template) return;
    try {
      const res = await apiFetch(`/api/v1/admin/templates/${template.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await readError(res)).message);
      toast.success('Шаблон архивирован');
      navigate('/templates', { replace: true });
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось архивировать шаблон');
    }
  };

  const isNew = template?.status === 'new';
  const isDraft = template?.status === 'draft';
  const clientTitle = template?.clientId
    ? (clients.find((c) => c.id === template.clientId)?.title ?? null)
    : null;
  const nextTemplateTitle = template?.nextVisitTemplateId
    ? (allTemplates.find((t) => t.id === template.nextVisitTemplateId)?.title ?? null)
    : null;

  return {
    templateId,
    template,
    clients,
    chainTemplates,
    form,
    mode,
    titleError,
    photos,
    isUploadingPhoto,
    isLoading,
    notFound,
    isSaving,
    isStatusUpdating,
    isTaskMutating,
    isStaff,
    isNew,
    isDraft,
    clientTitle,
    nextTemplateTitle,
    setField,
    startEdit,
    cancelEdit,
    handleSave,
    handleStatusChange,
    handleUploadPhoto,
    handleDeletePhoto,
    handleAddTask,
    handleUpdateTask,
    handleDeleteTask,
    handleMoveTask,
    handleArchive,
  };
}
